import React from 'react';
import {
  Document, Page, View, Text, StyleSheet, Image,
} from '@react-pdf/renderer';
import { registerCairoFont } from './pdfTextUtils';

registerCairoFont();

const NAVY = '#0F2C5C';
const BLUE = '#1E50A2';
const LGRAY = '#F4F6FA';
const MGRAY = '#D9DDE5';
const DGRAY = '#5B6473';
const WHITE = '#FFFFFF';
const BLACK = '#1A1F2A';
const GREEN = '#0E8A4F';
const RED = '#C62B2B';

const I18N = {
  en: {
    statement: 'DRIVER ACCOUNT STATEMENT',
    driver: 'Driver',
    phone: 'Phone',
    code: 'Code',
    period: 'Period',
    issuedOn: 'Issued on',
    from: 'From',
    to: 'To',
    summary: 'Summary',
    totalTrips: 'Total trip amount',
    totalPaid: 'Total paid',
    balance: 'Remaining balance',
    transactions: 'Transactions',
    date: 'Date',
    type: 'Type',
    description: 'Description',
    credit: 'Credit (Driver)',
    debit: 'Debit (Paid)',
    runningBalance: 'Balance',
    trip: 'Trip',
    payment: 'Payment',
    none: 'No transactions in this period',
    page: 'Page',
    of: 'of',
    currency: 'SAR',
    pending: 'Pending',
  },
  ar: {
    statement: 'كشف حساب السائق',
    driver: 'السائق',
    phone: 'الهاتف',
    code: 'الرمز',
    period: 'الفترة',
    issuedOn: 'تاريخ الإصدار',
    from: 'من',
    to: 'إلى',
    summary: 'الملخص',
    totalTrips: 'إجمالي مبلغ الرحلات',
    totalPaid: 'إجمالي المدفوع',
    balance: 'الرصيد المتبقي',
    transactions: 'الحركات',
    date: 'التاريخ',
    type: 'النوع',
    description: 'الوصف',
    credit: 'دائن (للسائق)',
    debit: 'مدين (مدفوع)',
    runningBalance: 'الرصيد',
    trip: 'رحلة',
    payment: 'دفعة',
    none: 'لا توجد حركات خلال هذه الفترة',
    page: 'صفحة',
    of: 'من',
    currency: 'ر.س',
    pending: 'قيد التحديد',
  },
};

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (ms) => {
  if (!ms) return '-';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const S = StyleSheet.create({
  page: {
    fontFamily: 'Cairo',
    fontSize: 9,
    color: BLACK,
    backgroundColor: WHITE,
    paddingTop: 28,
    paddingBottom: 50,
    paddingHorizontal: 28,
  },
  topBar: { height: 5, backgroundColor: NAVY, marginBottom: 14, marginHorizontal: -28, marginTop: -28 },

  // Header
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  companyBlock: { flex: 1 },
  logoImg: { width: 48, height: 48, objectFit: 'contain' },
  logoPlaceholder: { width: 48, height: 48, backgroundColor: LGRAY, borderRadius: 4 },
  companyName: { fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 1 },
  companyMeta: { fontSize: 8, color: DGRAY },

  titleBar: {
    backgroundColor: NAVY,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginTop: 4,
    marginBottom: 8,
  },
  titleText: { fontSize: 11, fontWeight: 700, color: WHITE },

  // Meta strip
  metaStrip: {
    flexDirection: 'row',
    backgroundColor: LGRAY,
    borderWidth: 0.5,
    borderColor: MGRAY,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  metaItem: { flex: 1 },
  metaLabel: { fontSize: 7, color: DGRAY, marginBottom: 1 },
  metaValue: { fontSize: 8.5, color: BLACK, fontWeight: 700 },

  // Driver card
  driverCard: {
    flexDirection: 'row',
    borderWidth: 0.5,
    borderColor: MGRAY,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  driverField: { flex: 1 },
  driverLabel: { fontSize: 7, color: DGRAY, marginBottom: 1 },
  driverValue: { fontSize: 9, fontWeight: 700, color: BLACK },

  // Summary
  summaryGrid: { flexDirection: 'row', marginBottom: 8, gap: 6 },
  summaryCard: {
    flex: 1,
    borderWidth: 0.5,
    borderColor: MGRAY,
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: WHITE,
  },
  summaryLabel: { fontSize: 7, color: DGRAY, marginBottom: 2 },
  summaryValue: { fontSize: 10.5, fontWeight: 700, color: BLACK },
  summaryGreen: { color: GREEN },
  summaryRed: { color: RED },
  summaryBlue: { color: BLUE },

  // Section
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: NAVY,
    backgroundColor: LGRAY,
    borderLeftWidth: 3,
    borderLeftColor: BLUE,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 0,
  },

  // Table
  table: { borderWidth: 0.5, borderColor: MGRAY, borderTopWidth: 0 },
  thRow: {
    flexDirection: 'row',
    backgroundColor: NAVY,
  },
  th: {
    color: WHITE,
    fontSize: 8.5,
    fontWeight: 700,
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  tr: {
    flexDirection: 'row',
    borderTopWidth: 0.5,
    borderColor: MGRAY,
    minHeight: 18,
  },
  trAlt: { backgroundColor: '#FAFBFD' },
  td: {
    fontSize: 8.5,
    paddingVertical: 4,
    paddingHorizontal: 6,
    color: BLACK,
  },
  tdMuted: { color: DGRAY, fontSize: 8 },
  tdGreen: { color: GREEN, fontWeight: 700 },
  tdNavy: { color: NAVY, fontWeight: 700 },

  // Type badge in row
  badge: {
    fontSize: 7.5,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 2,
    fontWeight: 700,
  },
  badgeTrip: { backgroundColor: '#EEEAFB', color: '#6C3FB8' },
  badgePayment: { backgroundColor: '#E2F4EA', color: GREEN },

  // Totals row
  totalsRow: {
    flexDirection: 'row',
    backgroundColor: LGRAY,
    borderTopWidth: 0.5,
    borderColor: MGRAY,
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  totalsLabel: { fontSize: 9, fontWeight: 700, color: NAVY },
  totalsValue: { fontSize: 9, fontWeight: 700, color: NAVY, textAlign: 'right' },

  empty: {
    paddingVertical: 24,
    textAlign: 'center',
    color: DGRAY,
    fontSize: 9,
    borderWidth: 0.5,
    borderTopWidth: 0,
    borderColor: MGRAY,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 7.5,
    color: DGRAY,
    borderTopWidth: 0.5,
    borderColor: MGRAY,
    paddingTop: 6,
  },
});

const COL_WIDTHS = {
  date: 65,
  type: 50,
  desc: 'auto',
  credit: 75,
  debit: 75,
  balance: 75,
};

const DriverStatementDocument = ({
  driver = {},
  company = {},
  entries = [],
  totals = { totalEarned: 0, totalPaid: 0, balance: 0 },
  dateFrom,
  dateTo,
  lang = 'en',
}) => {
  const T = I18N[lang] || I18N.en;
  const isRtl = lang === 'ar';
  const align = (extra = {}) => ({ textAlign: isRtl ? 'right' : 'left', ...extra });
  const alignEnd = (extra = {}) => ({ textAlign: isRtl ? 'left' : 'right', ...extra });
  const rowDir = isRtl ? 'row-reverse' : 'row';

  const issuedOn = fmtDate(Date.now());

  return (
    <Document title={`Driver Statement - ${driver.name || ''}`}>
      <Page size="A4" style={S.page}>
        <View style={S.topBar} fixed />

        {/* Header */}
        <View style={[S.headerRow, { flexDirection: rowDir }]}>
          <View style={S.companyBlock}>
            <Text style={[S.companyName, align()]}>
              {company.name || (isRtl ? 'الشركة' : 'Company')}
            </Text>
            {company.address && <Text style={[S.companyMeta, align()]}>{company.address}</Text>}
            {company.phone && <Text style={[S.companyMeta, align()]}>{company.phone}</Text>}
            {company.email && <Text style={[S.companyMeta, align()]}>{company.email}</Text>}
          </View>
          {company.logo ? (
            <Image src={company.logo} style={S.logoImg} />
          ) : (
            <View style={S.logoPlaceholder} />
          )}
        </View>

        {/* Title */}
        <View style={S.titleBar}>
          <Text style={[S.titleText, { textAlign: 'center' }]}>{T.statement}</Text>
        </View>

        {/* Meta strip */}
        <View style={[S.metaStrip, { flexDirection: rowDir }]}>
          <View style={S.metaItem}>
            <Text style={[S.metaLabel, align()]}>{T.from}</Text>
            <Text style={[S.metaValue, align()]}>{dateFrom || '-'}</Text>
          </View>
          <View style={S.metaItem}>
            <Text style={[S.metaLabel, align()]}>{T.to}</Text>
            <Text style={[S.metaValue, align()]}>{dateTo || '-'}</Text>
          </View>
          <View style={S.metaItem}>
            <Text style={[S.metaLabel, align()]}>{T.issuedOn}</Text>
            <Text style={[S.metaValue, align()]}>{issuedOn}</Text>
          </View>
        </View>

        {/* Driver card */}
        <View style={[S.driverCard, { flexDirection: rowDir }]}>
          <View style={S.driverField}>
            <Text style={[S.driverLabel, align()]}>{T.driver}</Text>
            <Text style={[S.driverValue, align()]}>{driver.name || '-'}</Text>
          </View>
          <View style={S.driverField}>
            <Text style={[S.driverLabel, align()]}>{T.phone}</Text>
            <Text style={[S.driverValue, align()]}>{driver.phone || '-'}</Text>
          </View>
          {driver.code && (
            <View style={S.driverField}>
              <Text style={[S.driverLabel, align()]}>{T.code}</Text>
              <Text style={[S.driverValue, align()]}>{driver.code}</Text>
            </View>
          )}
        </View>

        {/* Summary cards */}
        <View style={[S.summaryGrid, { flexDirection: rowDir }]}>
          <View style={S.summaryCard}>
            <Text style={[S.summaryLabel, align()]}>{T.totalTrips}</Text>
            <Text style={[S.summaryValue, align()]}>
              {fmtMoney(totals.totalEarned)} {T.currency}
            </Text>
          </View>
          <View style={S.summaryCard}>
            <Text style={[S.summaryLabel, align()]}>{T.totalPaid}</Text>
            <Text style={[S.summaryValue, S.summaryGreen, align()]}>
              {fmtMoney(totals.totalPaid)} {T.currency}
            </Text>
          </View>
          <View style={S.summaryCard}>
            <Text style={[S.summaryLabel, align()]}>{T.balance}</Text>
            <Text
              style={[
                S.summaryValue,
                totals.balance > 0 ? S.summaryRed : totals.balance < 0 ? S.summaryBlue : null,
                align(),
              ]}
            >
              {fmtMoney(totals.balance)} {T.currency}
            </Text>
          </View>
        </View>

        {/* Section title */}
        <Text style={[S.sectionTitle, align()]}>{T.transactions}</Text>

        {/* Transactions table */}
        {entries.length === 0 ? (
          <Text style={S.empty}>{T.none}</Text>
        ) : (
          <View style={S.table}>
            <View style={[S.thRow, { flexDirection: rowDir }]} fixed>
              <Text style={[S.th, { width: COL_WIDTHS.date }, align()]}>{T.date}</Text>
              <Text style={[S.th, { width: COL_WIDTHS.type }, align()]}>{T.type}</Text>
              <Text style={[S.th, { flex: 1 }, align()]}>{T.description}</Text>
              <Text style={[S.th, { width: COL_WIDTHS.credit }, alignEnd()]}>{T.credit}</Text>
              <Text style={[S.th, { width: COL_WIDTHS.debit }, alignEnd()]}>{T.debit}</Text>
              <Text style={[S.th, { width: COL_WIDTHS.balance }, alignEnd()]}>{T.runningBalance}</Text>
            </View>
            {entries.map((e, idx) => (
              <View
                key={e.id}
                style={[S.tr, idx % 2 === 1 ? S.trAlt : null, { flexDirection: rowDir }]}
                wrap={false}
              >
                <Text style={[S.td, { width: COL_WIDTHS.date }, align()]}>
                  {fmtDate(e.dateMs)}
                </Text>
                <View style={[{ width: COL_WIDTHS.type, justifyContent: 'center', paddingHorizontal: 6 }]}>
                  <Text
                    style={[
                      S.badge,
                      e.type === 'trip' ? S.badgeTrip : S.badgePayment,
                      { textAlign: 'center' },
                    ]}
                  >
                    {e.type === 'trip' ? T.trip : T.payment}
                  </Text>
                </View>
                <View style={[{ flex: 1, paddingVertical: 4, paddingHorizontal: 6 }]}>
                  <Text style={[S.td, { padding: 0 }, align()]}>{e.description || ''}</Text>
                  {e.secondary ? (
                    <Text style={[S.td, S.tdMuted, { padding: 0 }, align()]}>{e.secondary}</Text>
                  ) : null}
                </View>
                <Text style={[S.td, { width: COL_WIDTHS.credit }, alignEnd()]}>
                  {e.type === 'trip'
                    ? (e.amount > 0 ? fmtMoney(e.amount) : T.pending)
                    : '-'}
                </Text>
                <Text style={[S.td, S.tdGreen, { width: COL_WIDTHS.debit }, alignEnd()]}>
                  {e.type === 'payment' ? fmtMoney(e.amount) : '-'}
                </Text>
                <Text style={[S.td, S.tdNavy, { width: COL_WIDTHS.balance }, alignEnd()]}>
                  {fmtMoney(e.balance)}
                </Text>
              </View>
            ))}

            {/* Totals row */}
            <View style={[S.totalsRow, { flexDirection: rowDir }]}>
              <Text style={[S.totalsLabel, { flex: 1 }, align()]}>{T.summary}</Text>
              <Text style={[S.totalsValue, { width: COL_WIDTHS.credit }, alignEnd()]}>
                {fmtMoney(totals.totalEarned)}
              </Text>
              <Text style={[S.totalsValue, { width: COL_WIDTHS.debit, color: GREEN }, alignEnd()]}>
                {fmtMoney(totals.totalPaid)}
              </Text>
              <Text style={[S.totalsValue, { width: COL_WIDTHS.balance }, alignEnd()]}>
                {fmtMoney(totals.balance)}
              </Text>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={[S.footer, { flexDirection: rowDir }]} fixed>
          <Text>{company.name || ''}</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${T.page} ${pageNumber} ${T.of} ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default DriverStatementDocument;
